import Phaser from "phaser";
import { SpriteLoader } from "../../sprite-loader";
import { ModernButton } from "../modern-button";
import { ItemDefinition } from "../../models/common";
import { playSound } from "../../utils/sound-utils";
import { COLORS } from "../colors";

export interface LoadoutWindowCallbacks {
  onTake: () => void;
  onSkip: () => void;
}

export class LoadoutWindow {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private uiLayer: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.uiLayer = uiLayer;
  }

  show(items: ItemDefinition[], callbacks: LoadoutWindowCallbacks): void {
    this.hide();

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const cx = gameWidth / 2;

    const elements: Phaser.GameObjects.GameObject[] = [];

    // Semi-transparent overlay
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000000, 0.9);
    overlay.fillRect(0, 0, gameWidth, gameHeight);
    overlay.setScrollFactor(0);
    elements.push(overlay);

    // Layout constants
    const cardW = 260;
    const cardH = 60;
    const cardGap = 12;
    const titleH = 48;
    const buttonGap = 16;
    const buttonAreaH = 44;
    const totalH =
      titleH +
      items.length * (cardH + cardGap) -
      cardGap +
      buttonGap +
      buttonAreaH;
    let curY = (gameHeight - totalH) / 2;

    // Title
    const title = this.scene.add
      .text(cx, curY + titleH / 2, "FREE LOADOUT", {
        fontSize: "26px",
        color: "#5dd9e8",
        fontFamily: "Anton",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0);
    elements.push(title);
    curY += titleH;

    // One card per item
    items.forEach((def) => {
      const cardX = cx - cardW / 2;

      // Card background
      const card = this.scene.add.graphics();
      card.fillStyle(0x1e3348, 0.92);
      card.fillRoundedRect(cardX, curY, cardW, cardH, 10);
      card.lineStyle(2, 0x4a7a9b, 1);
      card.strokeRoundedRect(cardX, curY, cardW, cardH, 10);
      card.setScrollFactor(0);
      elements.push(card);

      // Sprite
      const spriteCX = cardX + 36;
      const spriteCY = curY + cardH / 2;
      const sprite = SpriteLoader.createSprite(
        this.scene,
        def.spriteSheet,
        def.spriteX,
        def.spriteY,
        spriteCX,
        spriteCY,
        true,
      );
      elements.push(sprite);

      // Item name
      const nameText = this.scene.add
        .text(cardX + 72, spriteCY - 9, def.name, {
          fontSize: "16px",
          color: "#ffffff",
          fontFamily: "Anton",
        })
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      elements.push(nameText);

      // Type label
      const typeText = this.scene.add
        .text(cardX + 72, spriteCY + 9, `${def.equipType ?? def.type}`, {
          fontSize: "12px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
        })
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      elements.push(typeText);

      curY += cardH + cardGap;
    });

    // Remove last gap, add space before buttons
    curY -= cardGap;
    curY += buttonGap;

    // TAKE and SKIP buttons side by side, centered
    const takeButton = new ModernButton({
      scene: this.scene,
      x: cx + 54,
      y: curY + buttonAreaH / 2,
      text: "TAKE",
      style: "success",
      onClick: () => {
        playSound(this.scene, "menu-click-sound", null, 0.5);
        this.hide();
        callbacks.onTake();
      },
    });
    takeButton.setScrollFactor(0);
    takeButton.setDepth(120001);
    elements.push(takeButton);

    const skipButton = new ModernButton({
      scene: this.scene,
      x: cx - 54,
      y: curY + buttonAreaH / 2,
      text: "SKIP",
      style: "secondary",
      onClick: () => {
        playSound(this.scene, "menu-click-sound", null, 0.5);
        this.hide();
        callbacks.onSkip();
      },
    });
    skipButton.setScrollFactor(0);
    skipButton.setDepth(120001);
    elements.push(skipButton);

    this.container = this.scene.add.container(0, 0, elements);
    this.container.setDepth(120000);
    this.container.setScrollFactor(0);
    this.uiLayer.add(this.container);
  }

  hide(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  isVisible(): boolean {
    return this.container !== null;
  }
}
